import { useQuery } from "@tanstack/react-query";
import type { CorgiResponse } from "@dealer-center/ddc-pages-fee-manager-interface";

export interface UseCorgiOptions {
  basePath: string;
  accountId: string;
  requestParams?: Record<string, string>;
}

export const useCorgi = ({
  basePath,
  accountId,
  requestParams
}: UseCorgiOptions) => {
  const trimmedAccountId = accountId.trim();

  return useQuery({
    queryKey: ["ddc-feemanager", "corgi", trimmedAccountId],
    queryFn: async (): Promise<string | null> => {
      // Percent-encoded, not URLSearchParams: API Gateway doesn't decode "+" as a space.
      const query = Object.entries({
        ...requestParams,
        tenantId: trimmedAccountId
      })
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&");
      const response = await fetch(`${basePath}/corgi?${query}`);
      // 404 means the corgi route isn't deployed yet; CC9 failures arrive as 502.
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(
          `Corgi request failed: ${response.status} ${response.statusText}`
        );
      }
      const body = (await response.json()) as CorgiResponse;
      return body.corgi ?? null;
    },
    enabled: trimmedAccountId !== "",
    // A settled corgi keys the mounted FeeManager; a late refetch would remount it mid-edit.
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: Infinity
  });
};
